import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronRight, Home, HelpCircle } from 'lucide-react';

function FAQPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqItems = [
    { q: "Qu'est-ce que copropriete.app ?", a: "copropriete.app est une solution de gestion de copropriété en ligne conçue pour les syndics bénévoles et les petites copropriétés. Elle permet de gérer les assemblées générales, les appels de fonds, la comptabilité et les documents." },
    { q: "Est-ce que copropriete.app est gratuit ?", a: "copropriete.app propose un gratuit de 14 jours sans engagement. Ensuite, l'abonnement est de 49€/mois ou 490€/an (2 mois offerts)." },
    { q: "Faut-il installer un logiciel ?", a: "Non, copropriete.app fonctionne entièrement en ligne via votre navigateur web. Accessible depuis n'importe quel appareil." },
    { q: "Qu'est-ce qu'un syndic bénévole ?", a: "Un syndic bénévole est un copropriétaire qui assure la gestion de la copropriété sans être rémunéré. C'est une alternative au syndic professionnel, adaptée aux petites copropriétés." },
    { q: "Comment devenir syndic bénévole ?", a: "Il faut être copropriétaire, se porter candidat lors d'une AG, et être élu à la majorité de l'article 25." },
    { q: "Quel est le délai de convocation d'une AG ?", a: "La convocation doit être envoyée au moins 21 jours avant la réunion (article 9 du décret du 17 mars 1967)." },
    { q: "Comment sont calculés les tantièmes ?", a: "Les tantièmes sont calculés selon la valeur relative de chaque lot (superficie, étage, exposition). Ils sont fixés dans l'EDD." },
    { q: "Peut-on générer des PDF de convocation et PV ?", a: "Oui, copropriete.app génère automatiquement convocations, feuilles de présence, procès-verbaux et appels de fonds en PDF." },
    { q: "Comment importer les données de ma copropriété ?", a: "Vous pouvez importer votre EDD en PDF et l'IA extrait automatiquement les lots, tantièmes et propriétaires." },
    { q: "Y a-t-il une application mobile ?", a: "copropriete.app est une application web responsive qui fonctionne parfaitement sur mobile sans installation." }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": { "@type": "Answer", "text": item.a }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>FAQ Copropriété - Questions fréquentes | copropriete.app</title>
        <meta name="description" content="Trouvez les réponses à vos questions sur la gestion de copropriété, le syndic bénévole, les assemblées générales, les charges et tantièmes." />
        <link rel="canonical" href="https://copropriete.app/faq" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold">copropriete.app</Link>
          <Link to="/login" className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg text-sm font-medium">Se connecter</Link>
        </div>
      </header>

      <nav className="max-w-4xl mx-auto px-4 py-4">
        <ol className="flex items-center gap-2 text-sm text-slate-500">
          <li><Link to="/" className="hover:text-indigo-600 flex items-center gap-1"><Home className="w-4 h-4" />Accueil</Link></li>
          <li>/</li>
          <li className="text-slate-900 font-medium">FAQ</li>
        </ol>
      </nav>

      <section className="max-w-4xl mx-auto px-4 py-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-2xl mb-6">
          <HelpCircle className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-4xl font-bold text-slate-900 mb-4">Questions fréquentes</h1>
        <p className="text-xl text-slate-600">Tout ce que vous devez savoir sur la gestion de copropriété</p>
      </section>

      <section className="max-w-4xl mx-auto px-4 pb-16">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {faqItems.map((faq, index) => (
            <div key={index} className="border-b border-slate-200 last:border-0">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full py-5 px-4 flex items-center justify-between text-left hover:bg-slate-50"
              >
                <h3 className="font-semibold text-slate-900 pr-4">{faq.q}</h3>
                {openIndex === index ? (
                  <ChevronDown className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-4 pb-5 text-slate-600">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Vous avez d'autres questions ?</h2>
          <p className="text-slate-400 mb-8">Contactez-nous ou essayez gratuitement copropriete.app</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-xl font-semibold">C'est gratuit 14 jours</Link>
            <a href="mailto:gmpdeveloppement@gmail.com" className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-3 rounded-xl font-semibold">Nous contacter</a>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm">
          <p>© 2026 copropriete.app - Tous droits réservés</p>
        </div>
      </footer>
    </div>
  );
}

export default FAQPage;
